<template>
  <div class="logs-page container mt-5">
    <div class="page-header mb-4">
      <div class="log-type-selector-wrapper">
        <b-form-select
          v-model="logType"
          :options="logTypeOptions"
          class="log-type-select-title"
        />
      </div>
    </div>
    
    <!-- Filtres et contrôles -->
    <div class="filters mb-4">
      <!-- Version mobile : filtres en ligne -->
      <div class="d-block d-md-none">
        <div class="filters-row-mobile d-flex align-items-end gap-2 mb-3">
          <div class="filter-group flex-fill">
            <label class="filter-label">Date de début</label>
            <b-form-input
              v-model="dateFrom"
              type="date"
              @change="onDateChange"
              class="filter-input"
            />
          </div>
          
          <div class="filter-group flex-fill">
            <label class="filter-label">Date de fin</label>
            <b-form-input
              v-model="dateTo"
              type="date"
              @change="onDateChange"
              class="filter-input"
            />
          </div>
        </div>
        
        <div class="filter-buttons">
          <b-button
            variant="primary"
            @click="fetchLogs"
            :disabled="loading"
            class="filter-btn me-2"
            block
          >
            <b-spinner small v-if="loading" class="me-2"></b-spinner>
            Actualiser
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="clearFilters"
            class="filter-btn"
            block
          >
            Effacer
          </b-button>
        </div>
      </div>
      
      <!-- Version desktop : filtres en ligne -->
      <div class="d-none d-md-block">
        <div class="filters-row d-flex align-items-center">
          <!-- Filtres de date -->
          <div class="filters-dates d-flex">
            <div class="filter-date-item me-3">
              <b-form-group label="Date de début :">
                <b-form-input
                  v-model="dateFrom"
                  type="date"
                  @change="onDateChange"
                />
              </b-form-group>
            </div>
            
            <div class="filter-date-item">
              <b-form-group label="Date de fin :">
                <b-form-input
                  v-model="dateTo"
                  type="date"
                  @change="onDateChange"
                />
              </b-form-group>
            </div>
          </div>
          
          <!-- Boutons d'action centrés verticalement -->
          <div class="filters-buttons ms-auto d-flex align-items-center">
            <b-button
              variant="primary"
              @click="fetchLogs"
              :disabled="loading"
              class="me-2"
            >
              <b-spinner small v-if="loading" class="me-2"></b-spinner>
              Actualiser
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="clearFilters"
            >
              Effacer
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistiques -->
    <div class="stats mb-4" v-if="stats">
      <div class="stats-columns">
        <div 
          class="stat-card" 
          :class="{ 'stat-card-selected': selectedStatuts.length === 0 }"
          @click="onTotalClick"
        >
          <h5>Total</h5>
          <span class="stat-number">{{ stats.total || 0 }}</span>
        </div>
        <!-- Statistiques pour les logs backup -->
        <template v-if="logType === 'backup'">
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('errors') }"
            @click="onStatutCardClick('errors')"
          >
            <h5>Erreurs</h5>
            <span class="stat-number text-danger">{{ stats.errors || 0 }}</span>
          </div>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('success') }"
            @click="onStatutCardClick('success')"
          >
            <h5>Succès</h5>
            <span class="stat-number text-success">{{ stats.success || 0 }}</span>
          </div>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('started') }"
            @click="onStatutCardClick('started')"
          >
            <h5>Démarrés</h5>
            <span class="stat-number text-primary">{{ stats.started || 0 }}</span>
          </div>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('finished') }"
            @click="onStatutCardClick('finished')"
          >
            <h5>Terminés</h5>
            <span class="stat-number text-info">{{ stats.finished || 0 }}</span>
          </div>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('prune') }"
            @click="onStatutCardClick('prune')"
          >
            <h5>Nettoyage</h5>
            <span class="stat-number text-warning">{{ stats.prune || 0 }}</span>
          </div>
        </template>
        <!-- Statistiques pour les logs notifications -->
        <template v-else>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('sent') }"
            @click="onStatutCardClick('sent')"
          >
            <h5>Envoyés</h5>
            <span class="stat-number text-success">{{ stats.sent || 0 }}</span>
          </div>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('errors') }"
            @click="onStatutCardClick('errors')"
          >
            <h5>Erreurs</h5>
            <span class="stat-number text-danger">{{ stats.errors || 0 }}</span>
          </div>
          <div 
            class="stat-card" 
            :class="{ 'stat-card-selected': isStatutSelected('invalid') }"
            @click="onStatutCardClick('invalid')"
          >
            <h5>Invalides</h5>
            <span class="stat-number text-warning">{{ stats.invalid || 0 }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- Liste des logs -->
    <div class="logs-list">
      <div v-if="loading" class="text-center py-4">
        <b-spinner></b-spinner>
        <p class="mt-2">Chargement des logs...</p>
      </div>
      
      <div v-else-if="logs.length === 0" class="text-center py-4">
        <p class="text-muted">Aucun log trouvé</p>
      </div>
      
      <div v-else>
        <div
          v-for="log in logs"
          :key="log.id"
          class="log-item"
          :class="getLogClass(log.statut || log.status)"
          @click="showLogDetail(log)"
        >
          <div class="log-row">
            <div class="log-status">
              <b-badge :variant="getStatusVariant(log.statut || log.status)">
                {{ log.statut || log.status }}
              </b-badge>
            </div>
            <div class="log-date">
              {{ formatDate(log.date || log.created_at) }}
            </div>
            <div class="log-message" v-html="formatLogMessageFirstLine(log)"></div>
          </div>
        </div>
      </div>
    </div>

      <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-container mt-4">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalLogs"
        :per-page="perPage"
        @change="onPageChange"
        class="justify-content-center"
      />
    </div>

    <!-- Modal de détail -->
    <b-modal
      v-model="showDetailModal"
      :title="`Détail du log #${selectedLog?.id}`"
      size="lg"
      @hidden="selectedLog = null"
    >
      <div v-if="selectedLog">
        <div class="row mb-3">
          <div class="col-md-6">
            <strong>Statut :</strong>
            <b-badge :variant="getStatusVariant(selectedLog.statut || selectedLog.status)" class="ms-2">
              {{ selectedLog.statut || selectedLog.status }}
            </b-badge>
          </div>
          <div class="col-md-6">
            <strong>Date :</strong> {{ formatDate(selectedLog.date || selectedLog.created_at) }}
          </div>
        </div>
        
        <!-- Informations spécifiques aux logs backup -->
        <div v-if="logType === 'backup' && selectedLog.nomdb" class="mb-3">
          <strong>Base de données :</strong> {{ selectedLog.nomdb }}
        </div>
        
        <!-- Informations spécifiques aux logs notifications -->
        <template v-if="logType === 'notifications'">
          <div v-if="selectedLog.title" class="mb-2">
            <strong>Titre :</strong> {{ selectedLog.title }}
          </div>
          <div v-if="selectedLog.user_name" class="mb-2">
            <strong>Utilisateur :</strong> {{ selectedLog.user_name }}
          </div>
          <div v-if="selectedLog.device_name" class="mb-2">
            <strong>Device :</strong> {{ selectedLog.device_name }} ({{ selectedLog.platform }})
          </div>
          <div v-if="selectedLog.http_code" class="mb-2">
            <strong>Code HTTP :</strong> {{ selectedLog.http_code }}
          </div>
          <div v-if="selectedLog.fcm_status" class="mb-2">
            <strong>Statut FCM :</strong> {{ selectedLog.fcm_status }}
          </div>
          <div v-if="selectedLog.error_message" class="mb-2">
            <strong>Message d'erreur :</strong> {{ selectedLog.error_message }}
          </div>
        </template>
        
        <div class="mb-3">
          <strong>Message :</strong>
          <div class="log-detail-message" v-html="formatLogMessage(selectedLog)"></div>
        </div>
        
        <!-- Payload pour les notifications -->
        <div v-if="logType === 'notifications' && selectedLog.payload" class="mb-3">
          <strong>Payload :</strong>
          <pre class="log-detail-message">{{ JSON.stringify(selectedLog.payload, null, 2) }}</pre>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import api from '@/plugins/api';

export default {
  name: 'LogsPage',
  data() {
    return {
      // Données principales
      logs: [],
      stats: null,
      loading: false,
      
      // Type de logs
      logType: 'backup', // 'backup' ou 'notifications'
      logTypeOptions: [
        { value: 'backup', text: 'Logs Backup' },
        { value: 'notifications', text: 'Logs Notifications' }
      ],
      
      // Filtres
      selectedStatuts: [], // Array de statuts sélectionnés
      dateFrom: '',
      dateTo: '',
      filterTimeout: null,
      
      // Pagination
      currentPage: 1,
      perPage: 20,
      totalLogs: 0,
      totalPages: 0,
      
      // Modal
      showDetailModal: false,
      selectedLog: null
    };
  },
  computed: {
    /**
     * Vérifie s'il y a des filtres actifs
     */
    hasActiveFilters() {
      return this.selectedStatuts.length > 0 || this.dateFrom || this.dateTo;
    },
    
    /**
     * Mapping des cartes vers les statuts pour les logs backup
     */
    backupStatutMapping() {
      return {
        'errors': ['ERROR', 'ERROR_NOT_FOUND'],
        'success': ['SUCCESS'],
        'started': ['STARTED'],
        'finished': ['FINISHED'],
        'prune': ['PRUNE']
      };
    },
    
    /**
     * Mapping des cartes vers les statuts pour les logs notifications
     */
    notificationStatutMapping() {
      return {
        'sent': ['SENT'],
        'errors': ['ERROR'],
        'invalid': ['INVALID']
      };
    },
    
    /**
     * Options de statut selon le type de logs
     */
    statutOptions() {
      if (this.logType === 'notifications') {
        return [
          { value: null, text: 'Tous les statuts' },
          { value: 'SENT', text: 'Envoyé' },
          { value: 'ERROR', text: 'Erreur' },
          { value: 'INVALID', text: 'Invalide' }
        ];
      } else {
        return [
          { value: null, text: 'Tous les statuts' },
          { value: 'STARTED', text: 'Démarré' },
          { value: 'SUCCESS', text: 'Succès' },
          { value: 'ERROR', text: 'Erreur' },
          { value: 'PRUNE', text: 'Nettoyage' },
          { value: 'LAST_UPDATED', text: 'Dernière mise à jour' },
          { value: 'ERROR_NOT_FOUND', text: 'Erreur non trouvée' },
          { value: 'FINISHED', text: 'Terminé' }
        ];
      }
    }
  },
  watch: {
    /**
     * Surveille les changements de page pour recharger les logs
     * Se déclenche uniquement après le montage (pas à l'initialisation)
     */
    currentPage(newPage, oldPage) {
      // Éviter le déclenchement lors de l'initialisation
      if (oldPage !== undefined && newPage !== oldPage) {
        console.log('📄 Changement de page:', oldPage, '->', newPage);
        this.fetchLogs();
      }
    },
    
    /**
     * Surveille les changements de type de logs
     */
    logType(newType, oldType) {
      // Éviter le déclenchement lors de l'initialisation
      if (oldType !== undefined && newType !== oldType) {
        console.log('📋 Changement de type de logs:', oldType, '->', newType);
        this.onLogTypeChange();
      }
    }
  },
  mounted() {
    this.fetchLogs();
    this.fetchStats();
  },
  beforeUnmount() {
    // Nettoyer le timeout si le composant est détruit
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    /**
     * Charge la liste des logs avec filtres
     */
    async fetchLogs() {
      this.loading = true;
      try {
        console.log('📋 fetchLogs - logType actuel:', this.logType);
        const params = {
          page: this.currentPage,
          limit: this.perPage,
          type: this.logType
        };
        console.log('📋 fetchLogs - params envoyés:', params);
        
        if (this.selectedStatuts.length > 0) {
          // Envoyer les statuts comme une chaîne séparée par des virgules
          params.statut = this.selectedStatuts.join(',');
        }
        
        if (this.dateFrom) {
          params.startDate = this.dateFrom;
        }
        
        if (this.dateTo) {
          params.endDate = this.dateTo;
        }
        
        const response = await api.get('/logs', { params });
        console.log('📊 Réponse API logs:', response.data);
        this.logs = response.data.data.logs || [];
        this.totalLogs = response.data.data.total || 0;
        this.totalPages = Math.ceil(this.totalLogs / this.perPage);
        console.log('📋 Logs chargés:', this.logs.length, 'sur', this.totalLogs);
        
      } catch (error) {
        console.error('Erreur lors du chargement des logs:', error);
        this.$bvToast.toast('Erreur lors du chargement des logs', {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Charge les statistiques des logs
     */
    async fetchStats() {
      try {
        const response = await api.get('/logs/stats', { 
          params: { type: this.logType } 
        });
        this.stats = response.data.data;
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      }
    },
    
    /**
     * Gère le changement de type de logs
     */
    onLogTypeChange() {
      // Réinitialiser les filtres et la pagination
      this.selectedStatuts = [];
      this.currentPage = 1;
      // Recharger les logs et les stats
      this.fetchLogs();
      this.fetchStats();
    },
    
    /**
     * Gère le clic sur une carte de statut
     */
    onStatutCardClick(cardKey) {
      const mapping = this.logType === 'backup' ? this.backupStatutMapping : this.notificationStatutMapping;
      const statuts = mapping[cardKey] || [];
      
      // Vérifier si ces statuts sont déjà sélectionnés
      const allSelected = statuts.every(statut => this.selectedStatuts.includes(statut));
      
      if (allSelected) {
        // Désélectionner : retirer tous les statuts de cette carte
        this.selectedStatuts = this.selectedStatuts.filter(statut => !statuts.includes(statut));
      } else {
        // Sélectionner : ajouter les statuts qui ne sont pas déjà sélectionnés
        statuts.forEach(statut => {
          if (!this.selectedStatuts.includes(statut)) {
            this.selectedStatuts.push(statut);
          }
        });
      }
      
      this.currentPage = 1;
      this.onFilterChange();
    },
    
    /**
     * Gère le clic sur la carte Total (réinitialise les filtres de statut)
     */
    onTotalClick() {
      this.selectedStatuts = [];
      this.currentPage = 1;
      this.onFilterChange();
    },
    
    /**
     * Vérifie si une carte de statut est sélectionnée
     */
    isStatutSelected(cardKey) {
      const mapping = this.logType === 'backup' ? this.backupStatutMapping : this.notificationStatutMapping;
      const statuts = mapping[cardKey] || [];
      return statuts.length > 0 && statuts.every(statut => this.selectedStatuts.includes(statut));
    },
    
    /**
     * Gère le changement de filtre avec délai
     */
    onFilterChange() {
      this.currentPage = 1; // Retour à la première page
      
      // Annuler le timeout précédent
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }
      
      // Délai de 300ms pour éviter trop de requêtes
      this.filterTimeout = setTimeout(() => {
        this.fetchLogs();
      }, 300);
    },

    /**
     * Gère le changement de date (filtrage immédiat)
     */
    onDateChange() {
      this.currentPage = 1; // Retour à la première page
      this.fetchLogs(); // Filtrage immédiat
    },

    /**
     * Gère le changement de page dans la pagination
     */
    onPageChange(page) {
      console.log('📄 Événement @change déclenché, nouvelle page:', page, 'currentPage actuel:', this.currentPage);
      // Mettre à jour currentPage si nécessaire (normalement déjà fait par v-model)
      if (page && page !== this.currentPage) {
        this.currentPage = page;
      }
      // Recharger les logs avec la nouvelle page
      this.fetchLogs();
    },

    /**
     * Retourne le label d'un statut
     */
    getStatutLabel(statut) {
      const option = this.statutOptions.find(opt => opt.value === statut);
      return option ? option.text : statut;
    },

    /**
     * Efface tous les filtres
     */
    clearFilters() {
      this.selectedStatuts = [];
      this.dateFrom = '';
      this.dateTo = '';
      this.currentPage = 1;
      this.fetchLogs();
    },
    
    /**
     * Affiche le détail d'un log
     */
    showLogDetail(log) {
      this.selectedLog = log;
      this.showDetailModal = true;
    },
    
    /**
     * Formate la date pour l'affichage
     */
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    /**
     * Masque les mots de passe dans un message
     */
    maskPasswords(message) {
      if (!message) return message;
      
      // Patterns pour détecter les mots de passe (insensible à la casse)
      const passwordPatterns = [
        /password\s*[:=]\s*([^\s\n,;]+)/gi,
        /mot\s+de\s+passe\s*[:=]\s*([^\s\n,;]+)/gi,
        /pwd\s*[:=]\s*([^\s\n,;]+)/gi,
        /mdp\s*[:=]\s*([^\s\n,;]+)/gi,
        /pass\s*[:=]\s*([^\s\n,;]+)/gi
      ];
      
      let maskedMessage = message;
      
      // Remplacer chaque pattern trouvé
      passwordPatterns.forEach(pattern => {
        maskedMessage = maskedMessage.replace(pattern, (match, passwordValue) => {
          // Extraire le préfixe (ex: "password:" ou "password =")
          const prefix = match.substring(0, match.length - passwordValue.length);
          // Retourner le préfixe + 5 astérisques
          return prefix + '*****';
        });
      });
      
      return maskedMessage;
    },
    
    /**
     * Formate le message du log pour l'affichage (première ligne seulement)
     */
    formatLogMessageFirstLine(log) {
      let message = '';
      
      if (this.logType === 'notifications') {
        // Pour les notifications, afficher title + body
        if (log.title && log.body) {
          message = `${log.title}: ${log.body}`;
        } else if (log.title) {
          message = log.title;
        } else if (log.body) {
          message = log.body;
        } else {
          message = 'Notification sans contenu';
        }
      } else {
        // Pour les logs backup, utiliser le champ log
        message = log.log || '';
      }
      
      if (!message) return '';
      
      // Masquer les mots de passe
      message = this.maskPasswords(message);
      
      // Prendre seulement la première ligne
      const firstLine = message.split('\n')[0];
      
      // Échapper les caractères HTML dangereux
      const escaped = firstLine
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      
      // Limiter la longueur si trop long
      const maxLength = 100;
      if (escaped.length > maxLength) {
        return escaped.substring(0, maxLength) + '...';
      }
      
      return escaped;
    },

    /**
     * Formate le message du log pour l'affichage complet (modal)
     */
    formatLogMessage(log) {
      let message = '';
      
      if (this.logType === 'notifications') {
        // Pour les notifications, afficher title + body
        if (log.title && log.body) {
          message = `${log.title}\n\n${log.body}`;
        } else if (log.title) {
          message = log.title;
        } else if (log.body) {
          message = log.body;
        } else {
          message = 'Notification sans contenu';
        }
      } else {
        // Pour les logs backup, utiliser le champ log
        message = log.log || '';
      }
      
      if (!message) return '';
      
      // Masquer les mots de passe
      message = this.maskPasswords(message);
      
      // Échapper les caractères HTML dangereux
      const escaped = message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      
      // Remplacer les retours à la ligne par des <br>
      return escaped.replace(/\n/g, '<br>');
    },
    
    /**
     * Retourne la classe CSS pour un log selon son statut
     */
    getLogClass(statut) {
      const classes = ['log-item'];
      switch (statut) {
        // Statuts backup
        case 'ERROR':
        case 'ERROR_NOT_FOUND':
          classes.push('log-error');
          break;
        case 'SUCCESS':
          classes.push('log-success');
          break;
        case 'STARTED':
          classes.push('log-started');
          break;
        case 'FINISHED':
          classes.push('log-finished');
          break;
        case 'PRUNE':
          classes.push('log-prune');
          break;
        case 'LAST_UPDATED':
          classes.push('log-updated');
          break;
        // Statuts notifications
        case 'SENT':
          classes.push('log-success');
          break;
        case 'INVALID':
          classes.push('log-prune');
          break;
        default:
          classes.push('log-default');
          break;
      }
      return classes.join(' ');
    },
    
    /**
     * Retourne la variante Bootstrap pour le badge de statut
     */
    getStatusVariant(statut) {
      switch (statut) {
        // Statuts backup
        case 'ERROR':
        case 'ERROR_NOT_FOUND':
          return 'danger';
        case 'SUCCESS':
          return 'success';
        case 'STARTED':
          return 'primary';
        case 'FINISHED':
          return 'info';
        case 'PRUNE':
          return 'warning';
        case 'LAST_UPDATED':
          return 'secondary';
        // Statuts notifications
        case 'SENT':
          return 'success';
        case 'INVALID':
          return 'warning';
        default:
          return 'light';
      }
    }
  }
};
</script>

<style scoped>
.logs-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.log-type-selector-wrapper {
  width: 100%;
  max-width: 400px;
  display: flex;
  justify-content: center;
}

.log-type-select-title {
  font-weight: 600;
  font-size: 1.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #007bff;
  border-radius: 8px;
  background: white;
  color: #2c3e50;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.log-type-select-title:hover {
  border-color: #0056b3;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.log-type-select-title:focus {
  border-color: #0056b3;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.filters {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

/* Styles pour la ligne des filtres desktop */
.filters-row {
  gap: 1rem;
  flex-wrap: wrap;
}

.filters-dates {
  flex: 1;
  min-width: 0;
}

.filter-date-item {
  flex: 1;
  min-width: 200px;
}

.filters-buttons {
  flex-shrink: 0;
}

/* Styles pour les filtres mobiles */
.filters-row-mobile {
  flex-wrap: wrap;
}

.filter-group {
  margin-bottom: 0;
  min-width: 0;
}

.filter-label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.filter-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9rem;
}

.filter-buttons {
  margin-top: 1rem;
}

.filter-btn {
  margin-bottom: 0.5rem;
  padding: 0.75rem;
  font-size: 0.9rem;
}

.stats {
  margin-bottom: 2rem;
}

.stats-columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.stat-card {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.stat-card:hover {
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.stat-card-selected {
  background: #e7f3ff;
  border: 2px solid #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.3);
}

.stat-card h5 {
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-number {
  font-size: 1.6rem;
  font-weight: bold;
  color: #2c3e50;
}

.logs-list {
  min-height: 400px;
}

.log-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  margin-bottom: 0.4rem;
}

.log-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  min-height: 36px;
}

.log-item:hover {
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.log-item.log-error {
  border-left: 4px solid #dc3545;
}

.log-item.log-success {
  border-left: 4px solid #28a745;
}

.log-item.log-started {
  border-left: 4px solid #007bff;
}

.log-item.log-finished {
  border-left: 4px solid #17a2b8;
}

.log-item.log-prune {
  border-left: 4px solid #ffc107;
}

.log-item.log-updated {
  border-left: 4px solid #6c757d;
}

.log-item.log-default {
  border-left: 4px solid #e9ecef;
}

.log-status {
  flex-shrink: 0;
  min-width: 70px;
}

.log-date {
  color: #6c757d;
  font-size: 0.8rem;
  flex-shrink: 0;
  min-width: 110px;
  text-align: right;
}

.log-message {
  line-height: 1.25;
  word-break: break-word;
  font-size: 0.85rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Compact badges inside list */
.log-item .badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
}

.log-db-id {
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

.log-detail-message {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Mobile: hauteur max et scroll pour le message */
@media (max-width: 768px) {
  .log-detail-message {
    max-height: 200px;
    overflow-y: auto;
    padding: 0.75rem;
    font-size: 0.85rem;
  }
}

.pagination-container {
  margin-top: 2rem;
}

/* Responsive */
@media (max-width: 768px) {
  .logs-page {
    padding: 0 10px;
  }
  
  .log-type-selector-wrapper {
    max-width: 100%;
  }
  
  .log-type-select-title {
    font-size: 1.25rem;
    padding: 0.6rem 1rem;
  }
  
  .filters {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .filter-group {
    margin-bottom: 0.75rem;
  }
  
  .filter-label {
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
  }
  
  .filter-input {
    font-size: 0.85rem;
    padding: 0.4rem;
  }
  
  .filter-btn {
    padding: 0.6rem;
    font-size: 0.85rem;
  }
  
  .stats-columns {
    gap: 6px;
  }
  
  .stat-card {
    margin: 0;
    padding: 0.75rem;
  }
  
  .stat-card h5 {
    font-size: 0.75rem;
    margin-bottom: 0.2rem;
  }
  
  .stat-number {
    font-size: 1.3rem;
  }
  
  .log-item {
    margin-bottom: 0.35rem;
    padding: 0.5rem 0.6rem;
  }
  
  .log-row {
    flex-direction: row;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: nowrap;
  }
  
  .log-status {
    min-width: 55px;
    flex-shrink: 0;
  }
  
  .log-date {
    text-align: left;
    min-width: 70px;
    font-size: 0.7rem;
    color: #6c757d;
    flex-shrink: 0;
  }
  
  .log-message {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 0.8rem;
    line-height: 1.2;
    flex: 1;
    min-width: 0;
  }
  
  .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }
  
  .pagination-container {
    margin-top: 1rem;
  }
  
  .pagination {
    justify-content: center;
  }
  
  .pagination .page-link {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }
}

/* Très petits écrans */
@media (max-width: 480px) {
  .logs-page {
    padding: 0 5px;
  }
  
  .log-type-select-title {
    font-size: 1.1rem;
    padding: 0.5rem 0.75rem;
  }
  
  .filters {
    padding: 0.75rem;
  }
  
  .stats-columns {
    gap: 4px;
  }
  .stat-card {
    padding: 0.6rem;
  }
  
  .stat-card h5 {
    font-size: 0.7rem;
  }
  
  .stat-number {
    font-size: 1.15rem;
  }
  
  .log-item {
    padding: 0.4rem 0.5rem;
    margin-bottom: 0.3rem;
  }
  
  .log-row {
    gap: 0.3rem;
  }
  
  .log-status {
    min-width: 50px;
  }
  
  .log-date {
    min-width: 60px;
    font-size: 0.65rem;
  }
  
  .log-message {
    font-size: 0.75rem;
  }
  
  .badge {
    font-size: 0.6rem;
    padding: 0.15rem 0.3rem;
  }
}
</style>
